import React from "react";

import { Component } from "../../../../commons/types/component";
import { ProductFinderSidebarPosition } from "../../../../commons/types/settings";
import cn from "../../libs/class-name";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import TileIconButton from "../TileIconButton/TileIconButton";

interface Props extends Component {
  children: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  sub?: string;
  closeButton?: React.ReactElement<React.ComponentProps<typeof TileIconButton>>;
  /** Opts out of the content-driven width, for children that change while the modal is open. */
  fixedWidth?: boolean;
  limitHeight?: boolean;
  alignPosition?: ProductFinderSidebarPosition;
  loadingIndicator?: React.ReactElement<React.ComponentProps<typeof LoadingIndicator>>;
}

const FilterModal = React.forwardRef(
  (
    {
      classNames = [],
      children,
      title,
      badge,
      description,

      sub = "Filter",
      closeButton,
      fixedWidth = false,
      limitHeight = false,
      alignPosition,
      loadingIndicator
    }: Props,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const childrenRef = React.useRef<HTMLDivElement>(null);
    const [asideMaxWidth, setAsideMaxWidth] = React.useState<number>(0);

    // To prevent long titles expanding the width of the `FilterModal`, cap the aside to the width of the children.
    // Note: This is currently necessary, because the children define the width of the `FilterModal` in combination with the fixed `filter-item-width` from the config.
    // A `ResizeObserver` is used instead of a one-time measurement, since the children's width changes with breakpoints and column count.
    React.useEffect(() => {
      const node = childrenRef.current;
      if (!node) {
        return undefined;
      }

      const resizeObserver = new ResizeObserver(() => {
        setAsideMaxWidth(node.offsetWidth);
      });
      resizeObserver.observe(node);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    return (
      <section
        ref={ref}
        className={cn(
          "FilterModal",
          [
            {
              fixedWidth,
              limitHeight,
              [`align-${alignPosition}`]: alignPosition
            }
          ],
          classNames
        )}
      >
        <div className="FilterModal__aside" style={{ maxWidth: asideMaxWidth }}>
          <div className="FilterModal__meta">
            <div className="FilterModal__sub">{sub}</div>
            <div className="FilterModal__titleContainer">
              <h2 className="FilterModal__title">{title}</h2>
              {badge && <div className="FilterModal__badge">{badge}</div>}
              {loadingIndicator && loadingIndicator}
            </div>

            {description && <div className="FilterModal__description">{description}</div>}
          </div>
          <div className="FilterModal__closeButton">{closeButton}</div>
        </div>
        <div ref={childrenRef} className="FilterModal__children">
          {children}
        </div>
      </section>
    );
  }
);

FilterModal.displayName = "FilterModal";

export default FilterModal;
